import { createFileRoute } from '@tanstack/react-router';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'Perfect Scientific Term Pronunciation';
const heroDescription =
  'AI voices that correctly pronounce complex scientific, medical, and technical terms. No more struggling with difficult terminology in research papers.';

const featureTitle = heroTitle;
const featureSubtitle = 'Every term pronounced correctly';
const featureDescription = heroDescription;
const featureBadge = 'Technical Excellence';
const featureVariant = 'default';
const featureCtaText = 'Try Scientific Pronunciation';

const featureFeatures = [
  {
    title: 'Medical Terminology',
    description:
      "Perfect pronunciation of complex medical terms like 'Mesothelioma' and 'Diphenhydramine'.",
  },
  {
    title: 'Scientific Nomenclature',
    description:
      'Accurate pronunciation of chemical compounds, biological species names, and technical jargon.',
  },
  {
    title: 'Continuous Learning',
    description:
      'Report mispronunciations and our AI learns to improve pronunciation accuracy over time.',
  },
];

const seoSections = [
  {
    title: 'Why Standard Text-to-Speech Fails at Scientific Terminology',
    content:
      'Standard text-to-speech systems achieve only 78% accuracy on scientific terminology because they\'re trained on general language datasets, not specialized academic content. This creates serious problems for medical students learning drug names like "acetaminophen," researchers presenting on "immunohistochemistry," or international medical graduates struggling with terms like "pneumonia" and "gastroenteritis." When technical terms are mispronounced, it disrupts comprehension, reduces professional credibility, and can even lead to medical misunderstandings.',
    contentSecondary:
      "Listening's AI voices, on the other hand, are trained on specialized academic content, achieving 95% accuracy on scientific terminology. This means you can listen to medical lectures, research papers, and technical documents with confidence, knowing that the AI will correctly pronounce even the most complex terms. Whether you're a medical student, researcher, or professional, Listening's AI voices are the perfect solution for accurate and reliable scientific term pronunciation.",
  },
  {
    title: '95% Scientific Accuracy Changes Everything for Academic Learning',
    content:
      'Our specialized AI voices achieve 95% accuracy on scientific terminology through targeted training on medical, biological, chemical, and technical vocabularies across multiple academic disciplines. This means terms like "electroencephalography," "pharmacokinetics," "heteroskedasticity," and "cytochrome P450" are pronounced correctly every time, enabling proper comprehension and professional confidence when listening to research papers, medical literature, or technical documentation.',
    contentSecondary:
      'This level of accuracy transforms the learning experience for medical students, researchers, and international professionals who previously had to pause constantly to look up pronunciations or guess at correct pronunciation. Users consistently report dramatically improved comprehension when technical content is spoken accurately, with testimonials noting "finally found voices that don\'t sound robotic when reading scientific papers" and "much better pronunciation than other AI voice tools for academic content." For professionals whose credibility depends on correct terminology, specialized pronunciation accuracy isn\'t just helpful - it\'s essential.',
  },
];

const slides = [
  {
    id: 1,
    title: 'The Scientific Pronunciation Crisis',
    subtitle: 'Medical Education Challenge',
    backgroundGradient: 'from-blue-50 via-purple-50 to-pink-50',
    numberGradient: 'from-red-500 to-orange-500',
    problem:
      "Medical words like 'fatigue' and 'phlegm' are often mispronounced by doctors because English is not phonetic",
    impact:
      'Reduced professional credibility, patient confusion, learning difficulties',
    solution: '95% accuracy on medical terminology',
    solutionDetail:
      'Specialized training on anatomical, pharmaceutical, and clinical vocabulary',
  },
  {
    id: 2,
    title: 'Research & Academic Challenge',
    subtitle: 'Time-Wasting Pronunciation Fixes',
    backgroundGradient: 'from-green-50 via-blue-50 to-purple-50',
    numberGradient: 'from-blue-500 to-cyan-500',
    problem:
      'Users spend hours experimenting with phonetic spellings to achieve desired pronunciation in TTS systems',
    impact: 'Wasted time, frustrated researchers, poor presentation quality',
    solution: 'Pre-trained accuracy on scientific terms',
    solutionDetail: 'Eliminates need for manual pronunciation fixes',
  },
  {
    id: 3,
    title: 'International Professional Challenge',
    subtitle: 'Non-Native Speaker Support',
    backgroundGradient: 'from-purple-50 via-pink-50 to-orange-50',
    numberGradient: 'from-purple-500 to-pink-500',
    problem:
      'International medical graduates and doctors from non-English speaking backgrounds struggle with medical terminology pronunciation',
    impact:
      'Communication barriers, reduced confidence, potential patient safety issues',
    solution: 'Clear, accurate pronunciation models',
    solutionDetail: 'For professional development and patient interaction',
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/scientific-pronunciation',
)({
  head: () => ({
    meta: [
      ...seo({
        title:
          'Scientific Term Pronunciation - AI Voices for Technical Content | Listening',
        description:
          'AI voices with 95% scientific term pronunciation accuracy. Perfect for medical, technical, and research content. No more mispronounced terminology',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
      sliderData={{
        slides,
        title: 'The Scientific Pronunciation Challenge',
        description:
          'Discover how pronunciation accuracy transforms professional communication',
        autoplayDelay: 8000,
      }}
    />
  ),
});
